import React, { useState } from "react";
import { playPitch, playFreq } from "@/lib/audio";
import { pitchToMidi, pitchToFreq } from "@/lib/music";

const WHITE = ["C", "D", "E", "F", "G", "A", "B"];
const BLACK_AFTER = { C: "C#", D: "D#", F: "F#", G: "G#", A: "A#" };
const W = 34, H = 130, BW = 22, BH = 80;

// Clickable keyboard. marks: letters to colour (e.g. ["C","F"]); highlight: { pitch: label }.
export const PianoKeyboard = ({ start = 3, octaves = 2, from, count, marks = [], highlight = {}, octaveLabels = false, cLabels = false, onPress, flash = {}, hideLetters = false, testId = "keyboard" }) => {
  const T = octaveLabels ? 22 : 0;
  const B = cLabels ? 20 : 0;
  const [down, setDown] = useState(null);
  const hi = Object.fromEntries(Object.entries(highlight).map(([p, l]) => [pitchToMidi(p), l]));
  const whites = [];
  if (from) {
    let li = WHITE.indexOf(from[0]);
    let o = Number(from.slice(1));
    for (let n = 0; n < count; n++) {
      whites.push({ l: WHITE[li], p: `${WHITE[li]}${o}` });
      li += 1;
      if (li === 7) { li = 0; o += 1; }
    }
  } else {
    for (let o = start; o < start + octaves; o++) WHITE.forEach((l) => whites.push({ l, p: `${l}${o}` }));
    whites.push({ l: "C", p: `C${start + octaves}` });
  }
  const press = (p) => { hideLetters ? playFreq(pitchToFreq(p), 0.9) : playPitch(p, 0.9); setDown(p); setTimeout(() => setDown(null), 220); onPress?.(p); };
  const fill = (p, base, mark) => {
    if (flash[p]) return flash[p];
    if (down === p) return "#F59E0B";
    if (hi[pitchToMidi(p)] !== undefined) return "#FCD38D";
    return mark ? "#FDE7C2" : base;
  };
  const width = whites.length * W;
  return (
    <svg viewBox={`0 0 ${width + 2} ${H + 2 + T + B}`} className="w-full max-w-full select-none" data-testid={testId} role="group" aria-label="Piano keyboard — use Tab and Enter to play keys">
      {octaveLabels && Array.from({ length: octaves }, (_, o) => (
        <g key={o}>
          <line x1={o * 7 * W + 1} y1={2} x2={o * 7 * W + 1} y2={T - 2} stroke="#A8A29E" />
          <text x={o * 7 * W + 3.5 * W} y={15} textAnchor="middle" fontSize="13" fontFamily="serif" fill="#A8A29E">{["1st", "2nd", "3rd", "4th", "5th", "6th", "7th"][o]} Octave</text>
        </g>
      ))}
      <g transform={`translate(0 ${T})`}>
      {whites.map((k, i) => {
        const mark = marks.includes(k.l);
        const lab = hi[pitchToMidi(k.p)];
        return (
          <g key={k.p} onClick={() => press(k.p)} style={{ cursor: "pointer" }} data-testid={`key-${k.p}`} role="button" tabIndex={0} aria-label={hideLetters ? `White key ${i + 1}` : `Play ${k.p}${lab ? `, ${lab}` : ""}`} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); press(k.p); } }} className="focus:outline-none focus-visible:[&>rect]:stroke-[3px] focus-visible:[&>rect]:stroke-amber-500">
            <rect x={i * W + 1} y={1} width={W - 1} height={H} rx={4} fill={fill(k.p, "#FFFDF8", mark)} stroke="#57534E" strokeWidth="1" />
            {!hideLetters && <text x={i * W + W / 2 + 1} y={H - 10} textAnchor="middle" fontSize="12" fontFamily="monospace" fill={mark ? "#B45309" : "#57534E"} fontWeight={mark ? 700 : 400}>{k.l}</text>}
            {lab && <text x={i * W + W / 2 + 1} y={H - 30} textAnchor="middle" fontSize="10" fontFamily="monospace" fill="#1C1917">{lab}</text>}
          </g>
        );
      })}
      {whites.slice(0, -1).map((k, i) => {
        const b = BLACK_AFTER[k.l];
        if (!b) return null;
        const p = `${b}${k.p.slice(-1)}`;
        const lab = hi[pitchToMidi(p)];
        return (
          <g key={p} onClick={() => press(p)} style={{ cursor: "pointer" }} data-testid={`key-${p}`} role="button" tabIndex={0} aria-label={hideLetters ? `Black key ${i + 1}` : `Play ${p.replace("#", " sharp ")}`} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); press(p); } }} className="focus:outline-none focus-visible:[&>rect]:stroke-[3px] focus-visible:[&>rect]:stroke-amber-500">
            <rect x={(i + 1) * W - BW / 2 + 1} y={1} width={BW} height={BH} rx={3} fill={flash[p] || (down === p ? "#B45309" : lab !== undefined ? "#92400E" : "#1C1917")} />
            {lab && lab.split(" ").map((part, j, all) => <text key={j} x={(i + 1) * W + 1} y={BH - 8 - (all.length - 1 - j) * 11} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="#FEF3C7">{part}</text>)}
          </g>
        );
      })}
      {cLabels && whites.map((k, i) => k.l === "C" && (
        <text key={`o-${k.p}`} x={i * W + W / 2 + 1} y={H + 16} textAnchor="middle" fontSize="12" fontWeight="700" fontFamily="monospace"
          fill={k.p === "C4" ? "#F59E0B" : "#A8A29E"} data-testid={`${testId}-octave-${k.p}`}>{k.p}</text>
      ))}
      </g>
    </svg>
  );
};
